import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { Course } from '@/client/courses';
import { DialogContext } from '@/common/context';
import { DialogType } from '@/system-settings/enums';
import { sortAlphabeticallyWithDKFirst } from '@/utils/helpers';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { SelectItemOptionsType } from 'primereact/selectitem';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDropdown = styled(Dropdown)`
  border-radius: var(--default-border-radius);
  border: 1px solid var(--purple-dark);
  background: var(--white-main);

  .p-dropdown-label.p-placeholder,
  .p-dropdown-label.p-inputtext {
    background: var(--white-main);
  }

  .p-dropdown-label.p-inputtext {
    padding-right: 0px;
    unicode-bidi: normal;
    direction: inherit;
  }

  &.player {
    width: 100%;
  }
`;

type CourseLanguageChangerProps = {
  course: Course;
  onChange: (value: LanguagesEnum) => void;
  language?: LanguagesEnum;
  shouldShowWarning?: boolean;
  isDisabled?: boolean;
  className?: string;
};

export const CourseLanguageChanger: React.FC<CourseLanguageChangerProps> = ({
  course,
  onChange,
  language,
  shouldShowWarning,
  isDisabled,
  className,
}) => {
  const { t } = useTranslation();
  const { setDialogData } = useContext(DialogContext);
  const [sortedCourseFiles, setSortedCourseFiles] =
    useState<SelectItemOptionsType>([]);

  useEffect(() => {
    const sortedUniqueCourseFiles = Array.from(
      new Map(
        sortAlphabeticallyWithDKFirst(course.courseLanguages).map(
          (language) => [
            language,
            { id: language, label: languageDisplayLabel[language] },
          ],
        ),
      ).values(),
    );

    setSortedCourseFiles(sortedUniqueCourseFiles);
  }, [course]);

  const onChangeHandler = (e: DropdownChangeEvent) => {
    if (shouldShowWarning) {
      setDialogData({
        type: DialogType.CONFIRMATION,
        show: true,
        header: t('dialog.content.language'),
        message: t('dialog.content.language.confirm'),
        onAccept: () => onChange(e.value),
      });
    } else {
      onChange(e.value);
    }
  };

  return (
    <label aria-label={t('dialog.course.language')}>
      <StyledDropdown
        id="course-language"
        placeholder={t('generic.select')}
        value={language}
        optionValue="id"
        optionLabel="label"
        onChange={onChangeHandler}
        options={sortedCourseFiles}
        className={className}
        panelClassName="course-language-panel"
        focusOnHover={false}
        disabled={isDisabled}
        filter
        filterInputAutoFocus
        {...preventDropdownBrowserTranslate}
      />
    </label>
  );
};
